import { useLayoutEffect, useState } from 'react';

/**
 * Walks up the DOM from `node` to find the nearest ancestor that scrolls horizontally.
 * The table element itself grows to its content width, so the element that actually
 * scrolls (and that sticky cells are pinned against) is an ancestor.
 */
const getScrollParentX = (node: HTMLElement | null): HTMLElement | null => {
  let current = node?.parentElement ?? null;
  while (current) {
    const { overflowX } = getComputedStyle(current);
    if (overflowX === 'auto' || overflowX === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

type PinnedSide = 'left' | 'right';

const isPinned = (cell: Element | null, side: PinnedSide) =>
  cell instanceof HTMLElement && cell.dataset.pinned === side;

type StickPoints = Record<PinnedSide, number[]>;

const firstRow = (table: HTMLElement) =>
  table.querySelector(':scope > thead > tr, :scope > tbody > tr');

const viewportToContentX = (scroller: HTMLElement) =>
  scroller.scrollLeft - scroller.getBoundingClientRect().left - scroller.clientLeft;

/**
 * The `scrollLeft` at which each pinned cell on `side` sticks and its unpinned neighbour starts
 * sliding beneath it: left cells are stuck past this point, right cells short of it. Measured on
 * resize so the scroll handler only compares numbers.
 */
const measureStickPoints = (table: HTMLElement, scroller: HTMLElement, side: PinnedSide) => {
  const row = firstRow(table);
  if (!row) {
    return [];
  }
  const toContentX = viewportToContentX(scroller);
  const neverSticks = side === 'left' ? Infinity : -Infinity;
  return Array.from(row.children)
    .filter((cell) => isPinned(cell, side))
    .map((cell) => {
      const neighbour = side === 'left' ? cell.nextElementSibling : cell.previousElementSibling;
      if (!(neighbour instanceof HTMLElement) || neighbour.dataset.pinned) {
        return neverSticks;
      }
      const style = getComputedStyle(cell);
      const inset = parseFloat(side === 'left' ? style.left : style.right);
      if (style.position !== 'sticky' || Number.isNaN(inset)) {
        return neverSticks;
      }
      const { width } = cell.getBoundingClientRect();
      const neighbourRect = neighbour.getBoundingClientRect();
      // Where the neighbour's facing edge sits in the content, less where the stuck cell's inner
      // edge rests in the scrollport. The half pixel absorbs subpixel rounding.
      const neighbourEdge =
        (side === 'left' ? neighbourRect.left : neighbourRect.right) + toContentX;
      const stuckEdge = side === 'left' ? inset + width : scroller.clientWidth - inset - width;
      return neighbourEdge - stuckEdge + (side === 'left' ? 0.5 : -0.5);
    });
};

/**
 * The `scrollLeft` at which the leading gutter sticks when the first left-pinned cell sits further
 * in than the gutter: once that cell has stuck and its own vacated slot fills the gutter, so the
 * gutter appears over nothing. Until then the columns before it scroll right to the edge.
 */
const measureLeadingGutterStickPoint = (table: HTMLElement, scroller: HTMLElement) => {
  const before = firstRow(table)?.querySelector(
    ':scope > [data-pinned="left"]',
  )?.previousElementSibling;
  if (!(before instanceof HTMLElement) || before.dataset.side || before.dataset.pinned) {
    return Infinity;
  }
  return before.getBoundingClientRect().right + viewportToContentX(scroller);
};

/**
 * Picks the pinned cell on each side that casts the shadow, as its 1-based position among that
 * side's pinned cells (what `:nth-child(n of [data-pinned])` in Table.tsx matches), or 0 for
 * none. When several are stuck, the innermost sits in front.
 */
const findShadowCasters = (stickPoints: StickPoints, scrollLeft: number) => ({
  left: stickPoints.left.map((point) => scrollLeft > point).lastIndexOf(true) + 1,
  right: stickPoints.right.map((point) => scrollLeft < point).indexOf(true) + 1,
});

/**
 * Reports whether the table's scroll container has room to scroll on the horizontal axis,
 * whether it is currently scrolled all the way to its left and right edges, which pinned
 * cells on each side have content scrolled beneath them, and whether a pinned column further in
 * has claimed the leading gutter.
 *
 * The right-edge signal lets consumers know when a right-stuck cell/column has been
 * scrolled into view and is resting at its natural position (so it no longer needs to
 * behave as if it were pinned).
 */
export const useCanScrollX = () => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [canScrollX, setCanScrollX] = useState(false);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(true);
  const [isScrolledToStart, setIsScrolledToStart] = useState(true);
  const [pinnedShadowLeft, setPinnedShadowLeft] = useState(0);
  const [pinnedShadowRight, setPinnedShadowRight] = useState(0);
  const [isLeadingGutterStuck, setIsLeadingGutterStuck] = useState(false);

  useLayoutEffect(() => {
    if (!element) {
      return undefined;
    }

    // The element that actually scrolls is an ancestor; fall back to the element itself
    // in the unlikely case it is its own scroll container.
    const scroller = getScrollParentX(element) ?? element;

    // Grace buffer so that nudging slightly away from the right edge doesn't immediately
    // hide a right-stuck column that is still effectively resting in place. We use the
    // stuck column's own width: only once it has been scrolled further than its footprint
    // is it genuinely pinned over other content.
    let endThreshold = 0;
    let stickPoints: StickPoints = { left: [], right: [] };
    let leadingGutterStickPoint = Infinity;
    const measure = () => {
      const stuckCell = element.querySelector<HTMLElement>('[data-stuck="right"]');
      endThreshold = stuckCell?.offsetWidth ?? 0;
      stickPoints = {
        left: measureStickPoints(element, scroller, 'left'),
        right: measureStickPoints(element, scroller, 'right'),
      };
      leadingGutterStickPoint = measureLeadingGutterStickPoint(element, scroller);
    };

    const update = () => {
      const canScroll = scroller.scrollWidth - scroller.clientWidth > 1;
      setCanScrollX(canScroll);
      const distanceToEnd = scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft;
      setIsScrolledToEnd(!canScroll || distanceToEnd <= endThreshold + 1);
      setIsScrolledToStart(!canScroll || scroller.scrollLeft <= 1);
      const shadowCasters = findShadowCasters(stickPoints, scroller.scrollLeft);
      setPinnedShadowLeft(canScroll ? shadowCasters.left : 0);
      setPinnedShadowRight(canScroll ? shadowCasters.right : 0);
      setIsLeadingGutterStuck(canScroll && scroller.scrollLeft >= leadingGutterStickPoint);
    };

    measure();
    update();
    scroller.addEventListener('scroll', update, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      measure();
      update();
    });
    resizeObserver.observe(scroller);
    resizeObserver.observe(element);

    return () => {
      scroller.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, [element]);

  return {
    ref: setElement,
    canScrollX,
    isScrolledToStart,
    isScrolledToEnd,
    pinnedShadowLeft,
    pinnedShadowRight,
    isLeadingGutterStuck,
  };
};
